import { FC } from 'react'
import { Link } from 'react-router-dom'
import { FaHome, FaArrowLeft, FaInfoCircle, FaBullseye, FaMapMarkerAlt, FaPhoneAlt } from 'react-icons/fa'
import { motion } from 'framer-motion'

const NotFound: FC = () => {
  const helpfulLinks = [
    { href: '/#about', icon: <FaInfoCircle />, label: 'About Us' },
    { href: '/#services', icon: <FaBullseye />, label: 'Our Services' },
    { href: '/#contact', icon: <FaMapMarkerAlt />, label: 'Visit Us' },
    { href: 'tel:+35724252225', icon: <FaPhoneAlt />, label: 'Call Now' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-off-white to-gray-200 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full text-center"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <img
            src="/assets/logo.jpg"
            alt="Studio Figura Logo"
            className="h-16 w-auto"
          />
          <span className="text-primary font-bold text-2xl uppercase">
            Studio Figura Larnaca
          </span>
        </div>

        {/* Error Code */}
        <div className="relative mb-6">
          <h1 className="text-8xl sm:text-9xl font-bold text-primary leading-none">
            404
          </h1>
          <div className="absolute left-1/4 top-1/4 text-6xl text-primary/10 animate-float">
            4
          </div>
          <div className="absolute right-1/4 bottom-1/4 text-6xl text-primary/10 animate-float"
               style={{ animationDelay: '1.5s' }}>
            4
          </div>
        </div>

        {/* Message */}
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-dark mb-4">
          Oops! Page Not Found
        </h2>
        
        <p className="text-text-gray text-lg mb-8">
          The page you're looking for seems to have vanished. Don't worry though - your journey to confidence and wellness is just a click away!
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
          <Link
            to="/"
            className="btn-primary justify-center"
          >
            <FaHome />
            <span>Go Back Home</span>
          </Link>
          
          <button
            onClick={() => window.history.back()}
            className="px-8 py-4 border-2 border-primary text-primary rounded-full font-medium hover:bg-primary hover:text-white transition-all duration-300 inline-flex items-center justify-center gap-2"
          >
            <FaArrowLeft />
            <span>Go Back</span>
          </button>
        </div>

        {/* Helpful Links */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
          <h3 className="text-xl font-semibold text-text-dark mb-6">
            Looking for something specific?
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {helpfulLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="flex items-center gap-3 p-4 bg-off-white rounded-xl hover:bg-primary hover:text-white transition-all duration-300 group"
              >
                <span className="text-primary group-hover:text-white text-xl transition-colors">
                  {link.icon}
                </span>
                <span className="font-medium">{link.label}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-8 bg-primary text-white rounded-xl p-6">
          <p className="font-semibold mb-2">Need immediate assistance?</p>
          <p className="mb-1">
            Call us:{' '}
            <a href="tel:+35724252225" className="underline hover:no-underline">
              +357 24 252225
            </a>
          </p>
          <p>
            Visit:{' '}
            <a
              href="https://www.google.com/maps/search/?api=1&query=34.910986258834214,33.62032849547252"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              149 Leoforos Faneromenis, Larnaca
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default NotFound
